/**
 * BasicInfoTab — user details display with field grid and groups list.
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { convertIsoUTCToLocalDate } from '../../../../libs.jsx';

/**
 * BadgeTile — single badge in the grid with a JS-driven hover popover.
 *
 * The popover is **portaled to document.body** with position: fixed so it
 * escapes every parent stacking context, overflow:hidden, and transform on
 * the way back up to the document root. Coordinates are recomputed on each
 * mouseenter from the badge's getBoundingClientRect.
 */
const POPOVER_W = 280;

const BadgeTile = ({ badge, tzOffset }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});

    const name = badge.badge_name || '';
    const desc = badge.badge_description || '';
    // create_time is a MySQL UTC timestamp; convert to campus-local date.
    const earnedDate = badge.create_time
        ? convertIsoUTCToLocalDate(badge.create_time, tzOffset)
        : '';
    const hasPopoverContent = !!(desc || earnedDate || name);

    const handleEnter = () => {
        if (!hasPopoverContent || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverW = Math.min(POPOVER_W, window.innerWidth - 16);
        // Center horizontally over the badge tile, then clamp inside the viewport.
        const centerX = rect.left + rect.width / 2;
        let left = Math.round(centerX - popoverW / 2);
        left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
        // Prefer placing the popover above the badge; fall back to below if
        // the badge is too close to the top of the viewport.
        const placeAbove = rect.top >= 140;
        const next = {
            position: 'fixed',
            left: `${left}px`,
            width: `${popoverW}px`,
            zIndex: 9999,
        };
        if (placeAbove) {
            next.bottom = `${Math.round(window.innerHeight - rect.top + 8)}px`;
        } else {
            next.top = `${Math.round(rect.bottom + 8)}px`;
        }
        setStyle(next);
        setOpen(true);
    };
    const handleLeave = () => setOpen(false);

    // Close on scroll — fixed position is relative to the viewport, so a
    // scroll would otherwise leave the popover floating away from its badge.
    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    return (
        <figure
            ref={ref}
            className="upp-badge"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onFocus={handleEnter}
            onBlur={handleLeave}
            tabIndex={hasPopoverContent ? 0 : -1}
        >
            {badge.badge_image ? (
                <img
                    className="upp-badge__img"
                    src={badge.badge_image}
                    alt={name || 'Earned badge'}
                    loading="lazy"
                />
            ) : (
                <div className="upp-badge__placeholder" aria-hidden="true">🏅</div>
            )}
            {name ? <figcaption className="upp-badge__label">{name}</figcaption> : null}

            {open && hasPopoverContent
                ? createPortal(
                    <span className="upp-badge__popover" role="tooltip" style={style}>
                        {name ? <span className="upp-badge__popover-title">{name}</span> : null}
                        {desc ? <span className="upp-badge__popover-desc">{desc}</span> : null}
                        {earnedDate ? <span className="upp-badge__popover-meta">Earned {earnedDate}</span> : null}
                    </span>,
                    document.body
                )
                : null}
        </figure>
    );
};

const BasicInfoTab = ({ basicData, userGroups, editRights, higherRole, onEdit, isGrandstream = false, badges = [], tzOffset }) => {
    if (!basicData) return null;

    return (
        <>
            {/* User Details */}
            <div className="pp-section">
                <div className="pp-section__header">
                    <h3 className="pp-section__title">User Details</h3>
                    {editRights && higherRole ? (
                        <div className="pp-section__actions">
                            <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
                                ✎ Edit
                            </button>
                        </div>
                    ) : null}
                </div>
                <div className="pp-section__body">
                    <div className="upp-info-grid">
                        <div className="pp-field">
                            <span className="pp-field__label">Username</span>
                            <span className="pp-field__value pp-field__value--mono">{basicData.username || '—'}</span>
                        </div>
                        <div className="pp-field">
                            <span className="pp-field__label">Display Name</span>
                            <span className="pp-field__value">{basicData.display_name || '—'}</span>
                        </div>
                        <div className="pp-field">
                            <span className="pp-field__label">Email</span>
                            <span className="pp-field__value">{basicData.email || '—'}</span>
                        </div>
                        <div className="pp-field">
                            <span className="pp-field__label">Phone</span>
                            <span className="pp-field__value">{basicData.phone || '—'}</span>
                        </div>
                        <div className="pp-field">
                            <span className="pp-field__label">Role</span>
                            <span className="pp-field__value">
                                <span className="upp-role-badge">{basicData.user_role_name || '—'}</span>
                            </span>
                        </div>
                        <div className="pp-field">
                            <span className="pp-field__label">Title</span>
                            <span className="pp-field__value">{basicData.title || '—'}</span>
                        </div>
                        {/* SIP Extension — only relevant on grandstream campuses;
                            hidden entirely elsewhere to avoid empty / confusing fields. */}
                        {isGrandstream ? (
                            <div className="pp-field">
                                <span className="pp-field__label">SIP Extension</span>
                                <span className="pp-field__value">{basicData.sip_extension || '—'}</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Groups */}
            <div className="pp-section">
                <div className="pp-section__header">
                    <h3 className="pp-section__title">
                        Groups <span className="pp-section__count">({(userGroups || []).length})</span>
                    </h3>
                </div>
                <div className="pp-section__body">
                    {(userGroups || []).length === 0 ? (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No groups assigned</span>
                    ) : (
                        <div className="upp-groups-list">
                            {userGroups.map((g, i) => (
                                <span key={g.group_id || i} className="upp-group-tag">{g.group_name}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Badges — earned via the loyalty/reward program. Each row carries a
                badge_image URL (S3); badge_name / badge_description are surfaced
                if present but the tile gracefully degrades to just the image. */}
            <div className="pp-section">
                <div className="pp-section__header">
                    <h3 className="pp-section__title">
                        Badges <span className="pp-section__count">({(badges || []).length})</span>
                    </h3>
                </div>
                <div className="pp-section__body">
                    {(badges || []).length === 0 ? (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No badges earned yet</span>
                    ) : (
                        <div className="upp-badges-grid">
                            {badges.map((b, i) => (
                                <BadgeTile key={b.badge_id || i} badge={b} tzOffset={tzOffset} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BasicInfoTab;
